import React, { memo, useCallback, useMemo } from "react";
import Modal from "@ui/Modal";
import ModalForm from "@ui/Modal/Form";
import t from "../../../../../i18n";
import type { DashboardModelT } from "../../schema/dashboard";
import useToasts from "@ui/Toast/useToasts";
import { Text, H4 } from "@ui/Text";
import { useMatch, useNavigate } from "react-router";
import { useDashboardDeleteCallback } from "../../hook/action/delete";
import AdditionnalMessage from "@ui/AdditionnalMessage";
import { css } from "@linaria/core";
import { spacings } from "@ui/Theme";
import { useDashboardLinkedFaultEvents } from "../../../../../api/useFaultEventsDashboards";
import { useTranslations } from "../../../../../api/useTranslations";
import { toPath } from "../../../../../router/helpers/path";
import { useSummaryDashboards } from "../../../../../api/useSummaryDashboards";
import { indexBy } from "diagnostics-lib/iterable";
export const ModalDashboardDelete = memo(
  ({
    dashboard,
    opened,
    close,
  }: {
    dashboard: DashboardModelT;
    opened: boolean;
    close: () => void;
  }) => {
    const translations = useTranslations();
    const deleteDashboardCallback = useDashboardDeleteCallback();
    const { addToast } = useToasts();
    const navigate = useNavigate();
    const linkedFaultEvents = useDashboardLinkedFaultEvents(dashboard.projectId, dashboard.id);

    const isMatchingDashboardRoute = useMatch(
      `/projects/${dashboard.projectId}/dashboards/${dashboard.id}`,
    );
    const summaryDashboards = useSummaryDashboards(dashboard.projectId);
    const indexedSummaryDashboards = useMemo(
      () => indexBy(summaryDashboards, "dashboardId"),
      [summaryDashboards],
    );
    const isLinkedToHome = indexedSummaryDashboards.has(dashboard.id);

    const deleteCallback = useCallback(
      () =>
        deleteDashboardCallback(dashboard)
          .then(() => {
            addToast(
              "success",
              t("project.dashboard.actions.delete.success", dashboard.title, translations),
            );
            if (isMatchingDashboardRoute)
              void navigate(
                toPath("/projects/:projectId/dashboards", {
                  projectId: dashboard.projectId,
                }),
              );
            return true;
          })
          .catch(() => {
            addToast(
              "error",
              t("project.dashboard.actions.delete.error", dashboard.title, translations),
            );
            return false;
          }),
      [
        addToast,
        dashboard,
        deleteDashboardCallback,
        isMatchingDashboardRoute,
        navigate,
        translations,
      ],
    );

    return (
      <Modal
        title={t("project.dashboard.transaction.delete.title", translations)}
        onRequestClose={close}
        isOpen={opened}
        size="l"
      >
        <ModalForm
          className={dashboardDeleteForm}
          onSubmit={deleteCallback}
          onRequestClose={close}
          okButtonName={t("project.dashboard.transaction.delete.submit_action")}
          okButtonVariant="destructive"
        >
          <Text type="body1">
            {t("project.dashboard.transaction.delete.subtitle", translations)}
          </Text>
          <H4>{dashboard.title}</H4>
          {dashboard.published === true && (linkedFaultEvents.length > 0 || isLinkedToHome) && (
            <AdditionnalMessage
              status="WARNING"
              text={t("project.dashboard.delete.message.linked_events", {
                linkedEventCount: linkedFaultEvents.length,
                isLinkedToHome,
                translations,
              })}
            />
          )}
        </ModalForm>
      </Modal>
    );
  },
);

const dashboardDeleteForm = css`
  row-gap: ${spacings.xs};
`;
